import React, { useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
} from "@mui/material";
import { StudentResultsView } from "../Components/SubmissionReview/StudentResultsView";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { ThinLayout } from "../Layouts/ThinLayout";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { useTranslation } from "react-i18next";
import { gql } from "@apollo/client";

const SUBMIT_STUDENT_REFLECTION = gql`
  mutation SubmitStudentReflection($activityId: String!, $message: String!) {
    submitStudentReflection(activityId: $activityId, message: $message) {
      ok
      message
    }
  }
`;

const GET_SUBMISSION_REVIEW_RESULTS = gql`
  query GetSubmissionReviewResults($activityId: ID!) {
    submissionReviewResults(activityId: $activityId) {
      activityName
      activityDescription
      submissionText
      submissionFileUrl
      submissionGrade
      instructorFeedback
      reviews {
        reviewerName
        submitted
        answers {
          id
          itemId
          value
          text
        }
      }
      rubricItems {
        id
        label
        itemType
        scale
        order
      }
      criterionSummaries {
        itemId
        label
        averageScore
        scoreCount
      }
    }
  }
`;

export function StudentSubmissionReviewResults() {
  const [searchParams] = useSearchParams();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();

  // Decode token
  const key = searchParams.get("key");
  let activityId = "";
  let resultsToken = "";
  try {
    const decoded = atob(key || "");
    const parts = decoded.split(":");
    activityId = parts[0];
    resultsToken = parts.slice(1).join(":");
  } catch {
    // Invalid key
  }

  const authHeaders = {
    headers: { Authorization: `JWT ${resultsToken}` },
  };

  const [reflection, setReflection] = useState("");
  const [reflectionSubmitted, setReflectionSubmitted] = useState(false);

  const [submitReflection] = useMutation(SUBMIT_STUDENT_REFLECTION, {
    onCompleted: () => setReflectionSubmitted(true),
  });

  const { loading, data, error } = useQuery(GET_SUBMISSION_REVIEW_RESULTS, {
    variables: { activityId },
    context: authHeaders,
    skip: !activityId,
    onError: (err) => setError(String(t("Error loading results")), err),
  });

  const results = data?.submissionReviewResults;

  if (loading) {
    return (
      <ThinLayout title={String(t("Loading..."))} loading={true}>
        <div />
      </ThinLayout>
    );
  }

  if (!results) {
    return (
      <ThinLayout title={String(t("Results"))}>
        <Typography variant="h5" sx={{ mt: 4, textAlign: "center" }}>
          {error
            ? String(t("Unable to load results. The link may have expired."))
            : String(t("Results not found."))}
        </Typography>
      </ThinLayout>
    );
  }

  return (
    <ThinLayout title={String(t(results.activityName))}>
      <StudentResultsView results={results}>
        {/* Close the Loop: Student Reflection */}
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardHeader
            title={String(t("Reflect on Your Feedback"))}
            titleTypographyProps={{ variant: "h6" }}
          />
          <CardContent>
            {reflectionSubmitted ? (
              <Typography color="success.main">
                {t("Thank you for your reflection!")}
              </Typography>
            ) : (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t(
                    "What did you learn from the feedback you received? How will you apply it?",
                  )}
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder={String(t("Share your reflection..."))}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!reflection.trim()}
                  onClick={() =>
                    submitReflection({
                      variables: {
                        activityId,
                        message: reflection,
                      },
                      context: authHeaders,
                    })
                  }
                >
                  {t("Submit Reflection")}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </StudentResultsView>
    </ThinLayout>
  );
}
